import clsx from "clsx";
import {
  type MotionProps,
  motion,
  type PanInfo,
  useDragControls,
  type Variants,
} from "motion/react";
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useKeyboardInset } from "@/shared/hooks/useKeyboardInset";
import { DialogCloseButton } from "@/shared/ui/overlay/DialogCloseButton";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import {
  blurFocusedElementWithin,
  useDialogFocusTrap,
} from "@/shared/ui/overlay/dialogFocus";
import { ModalContext } from "@/shared/ui/overlay/modal-context";
import { isModalDragHandleTarget } from "@/shared/ui/overlay/modalDragHandle";
import { stopOverlayWheelPropagation } from "@/shared/ui/overlay/overlayWheel";

/** ボトムシートが占めてよい可視領域の割合 (max-h-[85svh] と揃える) */
const BOTTOM_SHEET_MAX_HEIGHT_RATIO = 0.85;

export interface PersistentModalRenderProps {
  destroy: () => void;
}

interface PersistentModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode | ((props: PersistentModalRenderProps) => ReactNode);
  title?: ReactNode;
  position?: "center" | "bottom" | "right";
  headerActions?: ReactNode;
  flickToClose?: boolean;
  contentKey?: string | number;
}

export const PersistentModal: React.FunctionComponent<PersistentModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  position = "center",
  headerActions,
  flickToClose = true,
  contentKey,
}: PersistentModalProps) => {
  const { registerModal, unregisterModal } = useContext(ModalContext);
  const [depth, setDepth] = useState<number | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const dialogRef = useDialogFocusTrap<HTMLDivElement>(isOpen);
  const dragControls = useDragControls();
  const isBottomSheet = position === "bottom";
  // ソフトキーボードが下端を覆う量。開いている間だけ購読する
  const keyboardInset = useKeyboardInset(isOpen && isBottomSheet);
  const isKeyboardCovering = isBottomSheet && keyboardInset.bottom > 0;

  // 閉じる前にモーダル内のフォーカスを外し、ソフトキーボードを先に畳ませる。
  // フォーカスが入力欄に残ったまま閉じると、iOS Safari はキーボードを出したままにする。
  const requestClose = useCallback((): void => {
    blurFocusedElementWithin(dialogRef.current, document.activeElement);
    onCloseRef.current();
  }, [dialogRef]);

  // destroy(): childrenを完全にアンマウントしてから閉じる
  // 次回open時に自動でリセットされ、childrenはフレッシュにマウントされる
  const [isDestroyed, setIsDestroyed] = useState(false);

  const destroy = useCallback(() => {
    setIsDestroyed(true);
    requestClose();
  }, [requestClose]);

  // childrenがrender propの場合は{ destroy }を渡して解決
  const renderedChildren =
    typeof children === "function" ? children({ destroy }) : children;

  // 閉じアニメーション完了後に非表示にするためのstate
  // React推奨パターン: propsの変化に応じてレンダー中にstateを調整
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [hasExited, setHasExited] = useState(!isOpen);
  if (isOpen && hasExited) {
    setHasExited(false);
  }
  // isOpen時にdestroyed状態をリセット（次回openでchildrenをフレッシュにマウント）
  if (isOpen && isDestroyed) {
    setIsDestroyed(false);
  }
  const isHidden = !isOpen && hasExited;

  // 閉じアニメーション完了時に非表示化
  const handleAnimationComplete = (definition: string): void => {
    if (definition === "closed") {
      setHasExited(true);
    }
  };

  // モーダルが開いたときに深さとcloseハンドラーを登録
  useEffect(() => {
    if (isOpen) {
      const registration = registerModal(requestClose);
      let disposed = false;
      queueMicrotask(() => {
        if (!disposed) setDepth(registration.depth);
      });
      return (): void => {
        disposed = true;
        unregisterModal(registration.id);
        queueMicrotask(() => setDepth(null));
      };
    }
  }, [isOpen, registerModal, requestClose, unregisterModal]);

  // ボトムモーダルでのみ下フリック閉じを受け付ける
  const isFlickClosable = isBottomSheet && flickToClose;
  // キーボード表示中はビューポートが動いている最中で座標が定まらないため掴ませない
  const canStartFlick = isFlickClosable && !isKeyboardCovering;

  // ヘッダー（ハンドルと見出し行）からのみドラッグを開始する。
  // 閉じるボタンなどの操作部品の上では開始しない。
  const startFlickDrag = (event: React.PointerEvent<HTMLElement>): void => {
    if (!canStartFlick) return;
    if (!isModalDragHandleTarget(event.target)) return;
    dragControls.start(event);
  };

  // 下フリックでモーダルを閉じる処理
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ): void => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      requestClose();
    }
  };

  const getVariants = (): Variants => {
    switch (position) {
      case "bottom":
        return {
          open: { opacity: 1, y: 0 },
          closed: { opacity: 0, y: "100%" },
        };
      case "right":
        return {
          open: { opacity: 1, x: 0 },
          closed: { opacity: 0, x: "100%" },
        };
      default:
        return {
          open: { opacity: 1, scale: 1 },
          closed: { opacity: 0, scale: 0.95 },
        };
    }
  };

  // ボトムモーダルでフリック閉じが有効な場合のドラッグ設定。
  // dragListener を切り、開始点をヘッダーだけに絞る。パネル全体を開始点にすると
  // 本文のスクロールと同じポインタ列を奪い合い、どちらが動くか定まらない。
  const getDragProps = (): MotionProps => {
    if (isFlickClosable) {
      return {
        drag: "y" as const,
        dragListener: false,
        dragConstraints: { top: 0, bottom: 0 },
        dragElastic: { top: 0, bottom: 0.8 },
        onDragEnd: handleDragEnd,
        dragControls,
      };
    }
    return {};
  };

  // 深さに応じたz-indexを計算（各モーダルレベルで20ずつ増やす）
  const baseZIndex = 40;
  const zIndexOffset = (depth ?? 0) * 20;
  const backdropZIndex = baseZIndex + zIndexOffset;
  const modalZIndex = baseZIndex + 10 + zIndexOffset;

  return createPortal(
    <>
      {/* バックドロップ: 常時レンダリング、アニメーションで表示/非表示 */}
      <motion.div
        className="fixed inset-0 bg-black/60"
        aria-hidden={!isOpen}
        style={{
          zIndex: backdropZIndex,
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isOpen ? "auto" : "none",
        }}
        initial={false}
        animate={isOpen ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={requestClose}
        onWheel={stopOverlayWheelPropagation}
      />
      {/* モーダルパネル: 常時レンダリング、childrenの状態を保持 */}
      <motion.div
        ref={dialogRef}
        tabIndex={-1}
        {...getDialogAccessibilityProps(titleId, title != null)}
        aria-hidden={!isOpen}
        className={clsx(
          "fixed bg-card shadow-2xl",
          position === "center" &&
            "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl max-w-lg w-[calc(100%-2rem)] max-h-[85svh] overflow-hidden",
          position === "bottom" &&
            "bottom-0 left-0 right-0 rounded-t-2xl max-h-[85svh] overflow-hidden grid grid-flow-row grid-rows-[repeat(2,_max-content)_1fr]",
          position === "right" &&
            "top-0 right-0 bottom-0 w-80 max-w-full overflow-y-auto",
        )}
        style={{
          zIndex: modalZIndex,
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isOpen ? "auto" : "none",
          // キーボードに覆われている間だけ、その分持ち上げて可視領域へ収める。
          // 収まってしまえば入力欄はキーボードの上にあり、中身のスクロールは
          // ブラウザの標準動作に任せられる。覆いがない間はCSS側の指定に戻す。
          ...(isKeyboardCovering && {
            bottom: keyboardInset.bottom,
            maxHeight: Math.round(
              keyboardInset.height * BOTTOM_SHEET_MAX_HEIGHT_RATIO,
            ),
          }),
        }}
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={getVariants()}
        onAnimationComplete={handleAnimationComplete}
        {...getDragProps()}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onWheel={stopOverlayWheelPropagation}
      >
        {/* スワイプハンドル（ボトムモーダルでフリック閉じ有効時のみ表示） */}
        {isFlickClosable && (
          <div
            className="flex cursor-grab touch-none justify-center pb-1 pt-2 before:h-1 before:w-10 before:rounded-full before:bg-muted-foreground/30 before:content-[''] active:cursor-grabbing"
            onPointerDown={startFlickDrag}
          />
        )}
        {title != null && (
          <header
            className={clsx(
              "flex items-center justify-between gap-2 px-4 py-3 border-b border-border min-w-0",
              isFlickClosable && "touch-none",
            )}
            onPointerDown={startFlickDrag}
          >
            {typeof title === "string" ? (
              <h2
                id={titleId}
                className="text-lg font-semibold text-foreground"
              >
                {title}
              </h2>
            ) : (
              <h2 id={titleId} className="min-w-0 flex-1">
                {title}
              </h2>
            )}
            <fieldset
              aria-label="ダイアログ操作"
              className="m-0 flex min-w-0 shrink-0 items-center gap-1 border-0 p-0"
            >
              {headerActions}
              <DialogCloseButton
                onClose={requestClose}
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              />
            </fieldset>
          </header>
        )}
        <div
          key={contentKey}
          className="min-h-0 overflow-y-auto overscroll-contain"
        >
          {!isDestroyed && renderedChildren}
        </div>
      </motion.div>
    </>,
    document.body,
  );
};
